import api from "@/api/api";
import { InterestForm } from "@/models/customersTypes";
import { useState } from "react";
import daechlis from "@/public/svg/logo_daechlis.svg";
import { validateForm } from "@/helpers/validation";
import footerBg from '../../public/svg/obst-bg.svg';
import Modal from "@/components/UI/Modal";
import SeoHelmet from "@/components/UI/SeoHelmet";
import seoImg from "@/public/img/seo/produzentenformular.png";
import { track } from '@/helpers/analytics';

export default function InterestFormPage() {
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState<InterestForm>({
        contact_name: '',
        company_name: '',
        email: '',
        trade_type: 'Direktvertrieb',
        number_of_products: 0,
        product_categories: '',
        desired_start_date: new Date(),
        location: '',
        self_delivery: true,
        remarks: '',
    });

    const handleChange = (e: { target: { name: string; value: string | boolean } }) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: name === "number_of_products" ? Number(value) : value
        }));
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({
            ...prev,
            desired_start_date: new Date(e.target.value)
        }));
    };

    const handleSubmit = async () => {
        try {
            const validationErrors = validateForm(form);
            setFormErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) return;

            await api.interests.sendInterestForm(form);
            track('partner-interest-sent', { trade_type: form.trade_type });

            setShowModal(true);
        } catch (error) {
            console.error(error);
            track('form-error', { form: 'partner-interest' });
            alert('Fehler beim Senden des Formulars.');
        }
    };

    return (
        <section
            className="w-screen h-screen bg-cover bg-center relative"
            style={{ backgroundImage: `url(${footerBg})` }}
        >
            <SeoHelmet
                title="Interessentenformular für Produzenten"
                description="Füllen Sie unser Interessentenformular für Produzenten aus und wir nehmen Kontakt mit Ihnen auf."
                image={seoImg}
                url="https://www.yobsti.de/produzentenformular"
            />

            <div className="text-center space-y-4">
                <img src={daechlis} alt="Header Grafik Daechlis" loading="lazy" className="w-full m-0 block" />

                <h2 className="text-3xl font-bold pink-bg p-4 rounded-xl inline-block">
                    Interessentenformular für Produzenten
                </h2>
            </div>

            <div className="max-w-5xl mx-auto p-8 bg-white rounded-2xl shadow-lg space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Kontaktname</label>
                        <input
                            type="text"
                            name="contact_name"
                            placeholder="Kontaktname"
                            value={form.contact_name}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                            required
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Firmenname</label>
                        <input
                            type="text"
                            name="company_name"
                            placeholder="Firmenname"
                            value={form.company_name}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                            required
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">E-Mail</label>
                        <input
                            type="email"
                            name="email"
                            placeholder="E-Mail"
                            value={form.email}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                            required
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Vertriebsart</label>
                        <select
                            name="trade_type"
                            value={form.trade_type}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                            required
                        >
                            <option value="">Bitte wählen</option>
                            <option value="Direktvertrieb">Direktvertrieb</option>
                            <option value="Händler">Händler</option>
                        </select>
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Anzahl der Produkte</label>
                        <input
                            type="number"
                            name="number_of_products"
                            placeholder="Anzahl der Produkte"
                            value={form.number_of_products}
                            onChange={handleChange}
                            min="0"
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Produktkategorien</label>
                        <input
                            type="text"
                            name="product_categories"
                            placeholder="Produktkategorien"
                            value={form.product_categories}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Gewünschter Starttermin</label>
                        <input
                            type="date"
                            name="desired_start_date"
                            value={form.desired_start_date.toISOString().split('T')[0]}
                            onChange={handleDateChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium p-1 rounded">Standort</label>
                        <input
                            type="text"
                            name="location"
                            placeholder="Standort"
                            value={form.location}
                            onChange={handleChange}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                        />
                        {formErrors.fieldname && <p className="text-red-500 text-sm mt-1">{formErrors.fieldname}</p>}
                    </div>

                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            name="self_delivery"
                            checked={form.self_delivery}
                            onChange={(e) => handleChange({
                                target: {
                                    name: 'self_delivery',
                                    value: e.target.checked,
                                }
                            })}
                            className="w-5 h-5 text-lineGreen border-gray-300 rounded focus:ring-lineGreen"
                        />
                        <label htmlFor="self_delivery" className="text-sm font-medium p-1 rounded">
                            Selbstanlieferung
                        </label>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium p-1 rounded">Bemerkungen</label>
                        <textarea
                            name="remarks"
                            value={form.remarks}
                            onChange={handleChange}
                            rows={4}
                            className="w-full border border-gray-300 p-3 rounded focus:outline-none focus:ring-2 focus:ring-lineGreen"
                        />
                    </div>
                </div>

                <div className="text-center">
                    <button
                        onClick={handleSubmit}
                        className="bg-tertiary px-8 py-4 rounded-xl text-lg font-bold hover:bg-tertiaryLight transition-colors">
                        Formular absenden
                    </button>
                </div>
            </div>

            <Modal show={showModal} onClose={() => setShowModal(false)} />
        </section>
    );
}
